import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, CheckCircle } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import SelectField from '../common/SelectField.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import { getUnremittedPaidShipments, isShipmentDisabledForMl } from '../../utils/mlPaidSettlementRules.js';
import { useRequirePaidSettlementBeforeMl } from '../../hooks/useRequirePaidSettlementBeforeMl.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { canAddMlSheetShipments, hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import { useAuth } from '../../context/AuthContext.js';
import MlLocationChips from './MlLocationChips.js';
import MLSheetShipmentRecordList from './MLSheetShipmentRecordList.js';
import { toLocationsForMlDispatch } from '../../utils/hubDispatchFilter.js';
import {
  filterLocationsByQuery,
  formatLocationDropdownLabel,
} from '../../utils/locationDropdownLabel.js';
import {
  ML_MOBILE_DROPDOWN_ITEM,
  ML_MOBILE_DROPDOWN_ITEM_TEXT,
  ML_MOBILE_DROPDOWN_PANEL,
  ML_MOBILE_DROPDOWN_SEARCH,
  ML_MOBILE_FIELD_LABEL,
  ML_MOBILE_TRIGGER_TEXT,
  mlMobileTriggerClass,
} from '../../utils/mlSheetMobileDropdown.js';

const DispatchToMlSheet = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAgent = user?.role === 'Agent' || user?.role_name === 'Agent';
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [todaySheets, setTodaySheets] = useState([]);
  const [suggestedServices, setSuggestedServices] = useState([]);
  const [journeyDateLabel, setJourneyDateLabel] = useState('');
  const [locations, setLocations] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [fromIds, setFromIds] = useState(() => (
    user?.location_hub_id ? [user.location_hub_id] : []
  ));
  const [toIds, setToIds] = useState([]);
  const [serviceFilter, setServiceFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [selectedMlSheetId, setSelectedMlSheetId] = useState('');
  const [optionsLaneCompatible, setOptionsLaneCompatible] = useState(true);
  const [optionsLaneMessage, setOptionsLaneMessage] = useState('');
  const [sheetLaneCompatible, setSheetLaneCompatible] = useState(true);
  const [sheetLaneMessage, setSheetLaneMessage] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [locationSearch, setLocationSearch] = useState({ from: '', to: '' });
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const optionsToken = useRef(0);
  const shipmentToken = useRef(0);
  const optionsDebounceRef = useRef(null);
  const shipmentDebounceRef = useRef(null);

  const {
    loaded: mlBlockLoaded,
    mlCreationBlock,
    dues,
    agentDues,
    isHubAdmin,
    hardBlocked: mlHardBlocked,
    showGate: showPaidSettlementGate,
    settlementPath,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user);

  const requiresPaidBeforeMl = useRequirePaidSettlementBeforeMl(user);
  const shipmentDisabledForMl = (shipment) => isShipmentDisabledForMl(shipment, user, requiresPaidBeforeMl);

  useEffect(() => {
    if (!canAddMlSheetShipments(user)) {
      Alert.error('Dispatch to ML sheet is not available for this role');
      navigate('/ml-sheets');
      return undefined;
    }
    let cancelled = false;
    mlSheetService.getLocations()
      .then((locationsRes) => {
        if (cancelled) return;
        const locationsData = locationsRes.data?.hubs || locationsRes.data?.data || locationsRes.data || [];
        setLocations(Array.isArray(locationsData) ? locationsData : []);
      })
      .catch(() => {
        if (!cancelled) Alert.error('Failed to load branches');
      });
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  useEffect(() => {
    if (!locations.length || fromIds.length) return;
    const own = locations.find((loc) => (
      String(loc.id || loc.hub_id) === String(user?.location_hub_id)
      || (user?.city && (
        loc.location === user.city
        || loc.hub_name === user.city
        || loc.name === user.city
      ))
    ));
    if (!own) return;
    setFromIds([own.id || own.hub_id]);
  }, [locations, fromIds.length, user?.location_hub_id, user?.city]);

  const fromKey = fromIds.join(',');
  const toKey = toIds.join(',');

  const loadDispatchOptions = useCallback(async () => {
    const token = ++optionsToken.current;
    if (!fromIds.length || !toIds.length) {
      setTodaySheets([]);
      setSuggestedServices([]);
      setOptionsLaneCompatible(true);
      setOptionsLaneMessage('');
      setSelectedMlSheetId('');
      setServiceFilter('');
      setVehicleFilter('');
      setOptionsLoading(false);
      return;
    }
    try {
      setOptionsLoading(true);
      const response = await mlSheetService.getDispatchOptions({
        from_location_ids: fromKey,
        to_location_ids: toKey,
      });
      if (token !== optionsToken.current) return;
      const data = response.data || {};
      setTodaySheets(Array.isArray(data.sheets) ? data.sheets : []);
      setSuggestedServices(Array.isArray(data.suggested_services) ? data.suggested_services : []);
      setOptionsLaneCompatible(data.lane_compatible !== false);
      setOptionsLaneMessage(data.lane_message || '');
      setJourneyDateLabel(data.journey_date || '');
      setSelectedMlSheetId('');
      setServiceFilter('');
      setVehicleFilter('');
      setShipments([]);
      setSelected([]);
      setSheet(null);
      setSheetLaneCompatible(true);
      setSheetLaneMessage('');
    } catch (err) {
      if (token !== optionsToken.current) return;
      Alert.error(err.response?.data?.error || 'Could not load ML sheets for dispatch');
      setTodaySheets([]);
    } finally {
      if (token === optionsToken.current) setOptionsLoading(false);
    }
  }, [fromIds.length, toIds.length, fromKey, toKey]);

  useEffect(() => {
    if (!canAddMlSheetShipments(user)) return undefined;
    if (optionsDebounceRef.current) clearTimeout(optionsDebounceRef.current);
    optionsDebounceRef.current = setTimeout(() => {
      loadDispatchOptions();
    }, 220);
    return () => {
      if (optionsDebounceRef.current) clearTimeout(optionsDebounceRef.current);
    };
  }, [user, loadDispatchOptions]);

  useEffect(() => {
    shipmentToken.current += 1;
    setOptionsLaneCompatible(true);
    setOptionsLaneMessage('');
    setSheetLaneCompatible(true);
    setSheetLaneMessage('');
    setShipments([]);
    setSelected([]);
    setSheet(null);
  }, [fromKey, toKey]);

  const branchPairSelected = fromIds.length > 0 && toIds.length > 0;

  const locationLabelById = useMemo(() => {
    const map = new Map();
    (locations || []).forEach((loc) => {
      const id = loc.id ?? loc.hub_id;
      if (id == null) return;
      map.set(Number(id), formatLocationDropdownLabel(loc));
    });
    return map;
  }, [locations]);

  const formatRouteStopLine = useCallback((stopIds) => {
    if (!Array.isArray(stopIds) || !stopIds.length) return '';
    return stopIds
      .map((id) => locationLabelById.get(Number(id)) || `#${id}`)
      .join(' → ');
  }, [locationLabelById]);

  const laneSheets = useMemo(() => {
    if (!branchPairSelected || !suggestedServices.length) return todaySheets;
    const allowedServices = new Set(
      suggestedServices
        .map((row) => String(row.service_number || '').trim().toUpperCase())
        .filter(Boolean),
    );
    const allowedRouteIds = new Set(
      suggestedServices
        .map((row) => Number(row.route_id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    if (!allowedServices.size && !allowedRouteIds.size) return todaySheets;
    return todaySheets.filter((row) => {
      const svc = String(row.service_number || '').trim().toUpperCase();
      if (svc && allowedServices.has(svc)) return true;
      const rid = Number(row.route_id);
      return Number.isFinite(rid) && allowedRouteIds.has(rid);
    });
  }, [todaySheets, suggestedServices, branchPairSelected]);

  const hasServiceSuggestions = suggestedServices.length > 0;
  const showServiceSection = laneSheets.length > 0 || hasServiceSuggestions;

  const serviceOptions = useMemo(() => {
    const values = new Set();
    laneSheets.forEach((row) => {
      const text = String(row.service_number || '').trim();
      if (text) values.add(text);
    });
    suggestedServices.forEach((row) => {
      const text = String(row.service_number || '').trim();
      if (text) values.add(text);
    });
    if (serviceFilter) values.add(serviceFilter);
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [laneSheets, suggestedServices, serviceFilter]);

  useEffect(() => {
    if (serviceFilter) return undefined;
    const suggested = suggestedServices
      .map((s) => String(s.service_number || '').trim())
      .filter(Boolean);
    if (!suggested.length && !laneSheets.length) return undefined;
    const match = suggested.find((svc) => laneSheets.some(
      (sheet) => String(sheet.service_number || '').trim().toUpperCase() === svc.toUpperCase(),
    ));
    if (match) {
      setServiceFilter(match);
      return undefined;
    }
    const sheetServices = [...new Set(
      laneSheets.map((s) => String(s.service_number || '').trim()).filter(Boolean),
    )];
    if (sheetServices.length === 1) {
      setServiceFilter(sheetServices[0]);
      return undefined;
    }
    if (suggested.length === 1) {
      setServiceFilter(suggested[0]);
    }
    return undefined;
  }, [laneSheets, suggestedServices, serviceFilter]);

  const vehicleOptions = useMemo(() => {
    if (!serviceFilter) return [];
    const wanted = serviceFilter.toUpperCase();
    return Array.from(new Set(
      laneSheets
        .filter((row) => String(row.service_number || '').trim().toUpperCase() === wanted)
        .map((row) => String(row.vehicle_number || '').trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b));
  }, [laneSheets, serviceFilter]);

  const matchingSheets = useMemo(() => {
    const service = serviceFilter.trim().toUpperCase();
    const vehicle = vehicleFilter.trim().toUpperCase();
    return laneSheets.filter((row) => {
      const sheetService = String(row.service_number || '').trim().toUpperCase();
      const sheetVehicle = String(row.vehicle_number || '').trim().toUpperCase();
      if (service && sheetService !== service) return false;
      if (vehicle && sheetVehicle !== vehicle) return false;
      return true;
    });
  }, [laneSheets, serviceFilter, vehicleFilter]);

  const selectedServiceMeta = useMemo(() => {
    if (!serviceFilter) return null;
    const wanted = serviceFilter.trim().toUpperCase();
    return suggestedServices.find(
      (row) => String(row.service_number || '').trim().toUpperCase() === wanted,
    ) || null;
  }, [suggestedServices, serviceFilter]);

  useEffect(() => {
    if (matchingSheets.length === 1) {
      const onlyId = matchingSheets[0].ml_sheet_id;
      setSelectedMlSheetId((current) => (current === onlyId ? current : onlyId));
      if (!vehicleFilter && matchingSheets[0].vehicle_number) {
        setVehicleFilter(String(matchingSheets[0].vehicle_number));
      }
      return undefined;
    }
    setSelectedMlSheetId((current) => {
      if (!current) return current;
      return matchingSheets.some((s) => s.ml_sheet_id === current) ? current : '';
    });
    return undefined;
  }, [matchingSheets, vehicleFilter]);

  useEffect(() => {
    if (!serviceFilter || vehicleFilter) return undefined;
    if (vehicleOptions.length === 1) setVehicleFilter(vehicleOptions[0]);
    return undefined;
  }, [serviceFilter, vehicleOptions, vehicleFilter]);

  const loadShipments = useCallback(async () => {
    const token = ++shipmentToken.current;
    const mlId = String(selectedMlSheetId || '').trim();
    if (!mlId || (!fromIds.length && !toIds.length)) {
      setShipments([]);
      setSelected([]);
      setSheet(null);
      setSheetLaneCompatible(true);
      setSheetLaneMessage('');
      setSearching(false);
      return;
    }
    try {
      setSearching(true);
      setSheetLaneCompatible(true);
      setSheetLaneMessage('');
      const response = await mlSheetService.getPickupAddableShipments(mlId, {
        from_location_ids: fromKey,
        to_location_ids: toKey,
      });
      if (token !== shipmentToken.current) return;
      const laneOk = response.data?.lane_compatible !== false;
      setSheetLaneCompatible(laneOk);
      setSheetLaneMessage(laneOk ? '' : (response.data?.lane_message || ''));
      setSheet(response.data?.ml_sheet || null);
      setShipments(response.data?.shipments || []);
      setSelected([]);
    } catch (err) {
      if (token !== shipmentToken.current) return;
      Alert.error(err.response?.data?.error || 'Failed to load shipments');
    } finally {
      if (token === shipmentToken.current) setSearching(false);
    }
  }, [selectedMlSheetId, fromKey, toKey, fromIds.length, toIds.length]);

  useEffect(() => {
    if (shipmentDebounceRef.current) clearTimeout(shipmentDebounceRef.current);
    shipmentDebounceRef.current = setTimeout(() => {
      loadShipments();
    }, 180);
    return () => {
      if (shipmentDebounceRef.current) clearTimeout(shipmentDebounceRef.current);
    };
  }, [loadShipments]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !fromRef.current?.contains(event.target)
        && !toRef.current?.contains(event.target)
      ) {
        setOpenDropdown(null);
        setLocationSearch({ from: '', to: '' });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const receiverLocations = useMemo(
    () => toLocationsForMlDispatch(locations, fromIds, {
      enabled: hasInternalHubDispatchControls(user),
    }),
    [locations, fromIds, user],
  );

  const locationLabel = (loc) => (
    formatLocationDropdownLabel(loc, false)
    || loc.location
    || loc.hub_name
    || loc.name
    || ''
  );

  const isSelected = (ids, locationId) => ids.some((id) => String(id) === String(locationId));

  const toggleLocation = (side, locationId) => {
    const idStr = String(locationId);
    if (side === 'from') {
      const already = fromIds.some((id) => String(id) === idStr);
      const nextFrom = already
        ? fromIds.filter((id) => String(id) !== idStr)
        : [...fromIds, locationId];
      const eligible = new Set(
        toLocationsForMlDispatch(locations, nextFrom, {
          enabled: hasInternalHubDispatchControls(user),
        }).map((loc) => String(loc.id || loc.hub_id)),
      );
      setFromIds(nextFrom);
      setToIds((currentTo) => currentTo.filter((id) => eligible.has(String(id))));
      return;
    }
    setToIds((current) => (
      current.some((id) => String(id) === idStr)
        ? current.filter((id) => String(id) !== idStr)
        : [...current, locationId]
    ));
  };

  const visibleShipments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return shipments;
    return shipments.filter((shipment) => {
      const hay = [
        shipment.shipment_id,
        shipment.sender_location,
        shipment.receiver_location,
        shipment.sender_contact,
        shipment.receiver_contact,
      ].join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [shipments, search]);

  const hasBranchFilter = fromIds.length > 0 || toIds.length > 0;
  const laneBlocked = branchPairSelected && (
    optionsLaneCompatible === false
    || (selectedMlSheetId && sheetLaneCompatible === false)
  );
  const laneWarningText = (
    selectedMlSheetId && sheetLaneCompatible === false
      ? sheetLaneMessage
      : optionsLaneMessage
  );
  const sheetServiceNumber = useMemo(() => {
    const fromRoute = sheet?.route?.route_id || sheet?.route?.service_number;
    if (fromRoute) return String(fromRoute).trim();
    const row = todaySheets.find((s) => s.ml_sheet_id === selectedMlSheetId);
    return String(row?.service_number || serviceFilter || '').trim();
  }, [sheet, todaySheets, selectedMlSheetId, serviceFilter]);

  const routeRestricted = Boolean(sheet?.route_id && sheetServiceNumber);

  const shipmentStatusClass = (status) => {
    const statusMap = {
      booked: 'bg-blue-100 text-blue-800',
      dispatched: 'bg-purple-100 text-purple-800',
      received: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return statusMap[String(status || '').toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const rowDisabled = (shipment) => !mlBlockLoaded || mlHardBlocked || shipmentDisabledForMl(shipment);

  const selectableIds = useMemo(
    () => visibleShipments.filter((shipment) => !rowDisabled(shipment)).map((shipment) => shipment.shipment_id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleShipments, mlBlockLoaded, mlHardBlocked, requiresPaidBeforeMl, user],
  );

  const selectAllChecked = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));

  const handleSelectAll = () => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    setSelected((prev) => {
      if (selectAllChecked) {
        const drop = new Set(selectableIds);
        return prev.filter((id) => !drop.has(id));
      }
      const have = new Set(prev);
      return [...prev, ...selectableIds.filter((id) => !have.has(id))];
    });
  };

  const toggle = (shipmentId) => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    const shipment = shipments.find((s) => s.shipment_id === shipmentId);
    if (shipment && shipmentDisabledForMl(shipment)) {
      Alert.error('This Paid shipment has unremitted collections. Settle it first.');
      return;
    }
    setSelected((prev) => (
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId]
    ));
  };

  const handleDispatch = async () => {
    if (!mlBlockLoaded) return;
    if (mlHardBlocked) {
      Alert.error(mlCreationBlock.reason || 'Dispatch is temporarily blocked');
      return;
    }
    if (!selectedMlSheetId) {
      Alert.error('Select an ML sheet');
      return;
    }
    const blockedSelected = requiresPaidBeforeMl
      ? getUnremittedPaidShipments(shipments, selected)
      : [];
    if (blockedSelected.length > 0) {
      Alert.error('Selected Paid shipment(s) have unremitted collections. Settle them first.');
      return;
    }
    if (!selected.length) {
      Alert.error('Select at least one shipment');
      return;
    }
    setSaving(true);
    try {
      await mlSheetService.addPickupShipments(selectedMlSheetId, selected);
      Alert.success(`${selected.length} shipment(s) dispatched on ${selectedMlSheetId}`);
      navigate(`/ml-sheets/view/${selectedMlSheetId}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to dispatch shipments');
    } finally {
      setSaving(false);
    }
  };

  const dispatchDisabled = !mlBlockLoaded || mlHardBlocked || laneBlocked
    || !selected.length || !selectedMlSheetId;

  const renderLocationDropdown = (side, label, selectedIds, options, searchValue) => (
    <div className="relative min-w-0" ref={side === 'from' ? fromRef : toRef}>
      <label className={ML_MOBILE_FIELD_LABEL}>{label}</label>
      <div
        onClick={() => setOpenDropdown(openDropdown === side ? null : side)}
        className={mlMobileTriggerClass(openDropdown === side)}
      >
        <span className={ML_MOBILE_TRIGGER_TEXT}>
          {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select locations'}
        </span>
        <span className={`shrink-0 text-[10px] sm:text-sm transition ${openDropdown === side ? 'rotate-180 text-blue-500' : 'text-gray-500'}`}>▾</span>
      </div>
      {openDropdown === side && (
        <div className={ML_MOBILE_DROPDOWN_PANEL}>
          <input
            type="text"
            placeholder="Search locations..."
            value={searchValue}
            onChange={(e) => setLocationSearch((prev) => ({ ...prev, [side]: e.target.value }))}
            autoFocus
            className={ML_MOBILE_DROPDOWN_SEARCH}
          />
          <div className="max-h-48 overflow-y-auto">
            {options.length > 0 ? options.map((loc) => {
              const locId = loc.id || loc.hub_id;
              return (
                <label key={locId} className={ML_MOBILE_DROPDOWN_ITEM}>
                  <input
                    type="checkbox"
                    checked={isSelected(selectedIds, locId)}
                    onChange={() => toggleLocation(side, locId)}
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                  />
                  <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>{locationLabel(loc)}</span>
                </label>
              );
            }) : (
              <p className="text-[10px] sm:text-sm text-gray-500 p-2">No locations found</p>
            )}
          </div>
        </div>
      )}
      <div className="mt-1 min-w-0">
        <MlLocationChips
          compact
          locations={locations}
          selectedIds={selectedIds}
          onRemove={(id) => toggleLocation(side, id)}
        />
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto pb-6">
        <ListPageHeader title="Dispatch to ML sheet" icon={Truck} backTo="/ml-sheets" className="mb-4 sm:mb-6">
          <Button
            icon={CheckCircle}
            loading={saving}
            disabled={dispatchDisabled}
            onClick={handleDispatch}
          >
            Dispatch selected ({selected.length})
          </Button>
        </ListPageHeader>

        <MlPaidSettlementGate
          mlCreationBlock={mlCreationBlock}
          dues={dues}
          agentDues={agentDues}
          show={showPaidSettlementGate}
          hardBlocked={mlHardBlocked}
          settleOpen={settleOpen}
          setSettleOpen={setSettleOpen}
          onSettleSubmitted={refreshMlSettlement}
          settlementPath={settlementPath}
          allowSettle={!isHubAdmin}
          className="mb-4"
        />

        <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Open ML sheets stay available until <span className="font-semibold">service arrival</span>,
          including trips that departed on an earlier date and arrive later (e.g. 31-Oct 17:45 → 01-Nov 21:00).
          Via stops on the service count when matching sender and receiver branches.
        </div>

        <div className="bg-white rounded-xl shadow p-3 sm:p-4 mb-4">
          <h3 className="font-semibold text-secondary text-sm sm:text-base mb-3">Sender &amp; receiver</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderLocationDropdown(
              'from',
              'Sender branch',
              fromIds,
              filterLocationsByQuery(locations, locationSearch.from),
              locationSearch.from,
            )}
            {renderLocationDropdown(
              'to',
              'Receiver branch',
              toIds,
              filterLocationsByQuery(receiverLocations, locationSearch.to),
              locationSearch.to,
            )}
          </div>
        </div>

        {hasBranchFilter && (
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 mb-4 space-y-3">
            <h3 className="font-semibold text-secondary text-sm sm:text-base">Service &amp; ML sheet</h3>
            {!branchPairSelected ? (
              <p className="text-sm text-gray-600">
                Select both sender and receiver branches to load matching service and ML sheet options.
              </p>
            ) : optionsLoading ? (
              <p className="text-sm text-gray-500">Loading sheets…</p>
            ) : showServiceSection ? (
              <>
                <div className="grid gap-2 min-w-0 grid-cols-1 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service number</label>
                    <SelectField
                      variant="form"
                      portal
                      searchable
                      searchPlaceholder="Search"
                      value={serviceFilter}
                      onChange={(e) => {
                        setServiceFilter(e.target.value);
                        setVehicleFilter('');
                        setSelectedMlSheetId('');
                      }}
                      className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    >
                      <option value="">Select service…</option>
                      {serviceOptions.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </SelectField>
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
                    <SelectField
                      variant="form"
                      portal
                      searchable={!isAgent}
                      searchPlaceholder="Search"
                      value={vehicleFilter}
                      onChange={(e) => {
                        setVehicleFilter(e.target.value);
                        setSelectedMlSheetId('');
                      }}
                      disabled={!serviceFilter || vehicleOptions.length === 0}
                      className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    >
                      <option value="">
                        {!serviceFilter ? 'Select service first…' : vehicleOptions.length ? 'Select vehicle…' : 'No vehicle'}
                      </option>
                      {vehicleOptions.map((vehicle) => (
                        <option key={vehicle} value={vehicle}>{vehicle}</option>
                      ))}
                    </SelectField>
                  </div>
                </div>
                {selectedServiceMeta ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 space-y-1">
                    <p>
                      <span className="font-medium">Route:</span>{' '}
                      {selectedServiceMeta.route_name || selectedServiceMeta.service_number}
                      {(selectedServiceMeta.departure_time || selectedServiceMeta.arrival_time) ? (
                        <>
                          {' '}
                          · {selectedServiceMeta.departure_time || '—'} – {selectedServiceMeta.arrival_time || '—'}
                        </>
                      ) : null}
                    </p>
                    {formatRouteStopLine(
                      selectedServiceMeta.route_stop_ids || selectedServiceMeta.via_location_ids,
                    ) ? (
                      <p className="text-gray-600">
                        <span className="font-medium">Stops (incl. via):</span>{' '}
                        {formatRouteStopLine(
                          selectedServiceMeta.route_stop_ids || selectedServiceMeta.via_location_ids,
                        )}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ML sheet <span className="text-red-500">*</span>
                  </label>
                  {matchingSheets.length === 0 ? (
                    <p className="text-sm text-amber-700">
                      {laneSheets.length === 0 && hasServiceSuggestions
                        ? 'Matching service found for this lane (via stops included), but no open ML sheet is available before arrival. Upload or open the trip log for this service.'
                        : 'No sheet for this service and vehicle before arrival.'}
                    </p>
                  ) : (
                    <SelectField
                      variant="form"
                      portal
                      value={selectedMlSheetId}
                      onChange={(e) => setSelectedMlSheetId(e.target.value)}
                      className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    >
                      {matchingSheets.length === 1 ? null : (
                        <option value="">Select ML sheet…</option>
                      )}
                      {matchingSheets.map((row) => (
                        <option key={row.ml_sheet_id} value={row.ml_sheet_id}>
                          {row.ml_sheet_id}
                          {row.journey_date ? ` · journey ${row.journey_date}` : ''}
                          {row.departure_time ? ` · dep ${row.departure_time}` : ''}
                          {row.arrival_time ? ` · arr ${row.arrival_time}` : ''}
                          {row.vehicle_number ? ` · ${row.vehicle_number}` : ''}
                          {row.service_number ? ` · ${row.service_number}` : ''}
                        </option>
                      ))}
                    </SelectField>
                  )}
                </div>
              </>
            ) : branchPairSelected && optionsLaneCompatible === false && optionsLaneMessage ? (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-3">
                {optionsLaneMessage}
              </p>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {optionsLaneMessage
                  || 'No open ML sheet before arrival matches this lane. Upload the trip log for the correct service.'}
              </p>
            )}
          </div>
        )}

        {laneBlocked && laneWarningText && selectedMlSheetId && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            {laneWarningText}
          </div>
        )}

        {routeRestricted && selectedMlSheetId && !laneBlocked && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Only booked shipments on service <span className="font-semibold">{sheetServiceNumber}</span> can be
            dispatched on this sheet. Others will be rejected.
          </div>
        )}

        {selectedMlSheetId && (
          <div className="bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 max-w-full">
            <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
              <h3 className="font-semibold text-secondary text-sm sm:text-base">
                Select shipments ({selected.length}/{visibleShipments.length})
              </h3>
              {searching && <span className="text-xs text-blue-600">Loading…</span>}
            </div>
            <div className="mb-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="GC number, location, or mobile"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
              />
            </div>
            <div className="min-w-0 max-w-full overflow-x-auto">
              {searching ? (
                <Loader fullScreen={false} size="small" message="Loading booked shipments…" />
              ) : laneBlocked && laneWarningText ? (
                <p className="text-sm text-amber-800 py-6 text-center">{laneWarningText}</p>
              ) : (
                <MLSheetShipmentRecordList
                  shipments={visibleShipments}
                  selectedShipmentIds={selected}
                  onToggle={toggle}
                  onSelectAll={handleSelectAll}
                  selectAllChecked={selectAllChecked}
                  isShipmentDisabled={rowDisabled}
                  getStatusColor={shipmentStatusClass}
                  emptyMessage={
                    routeRestricted
                      ? `No booked shipments on ${sheetServiceNumber} for the selected branches.`
                      : 'No booked shipments available to dispatch.'
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DispatchToMlSheet;
